import Vue from "vue";

/**
 * Modos de cor do sistema.
 *
 * O tema é um atributo `data-theme` no <html>; os valores vivem em
 * assets/styles/tokens.css (o :root é o Salvia, os blocos [data-theme=...]
 * sobrescrevem). Fica em localStorage, então **funciona sem login** e
 * sobrevive ao logout.
 *
 * Também espelha o tema no Vuetify, senão os `color="primary"` e o modo
 * escuro dos componentes dele ignorariam a troca.
 */


/**
 * Paletas do Vuetify por modo.
 *
 * O Vuetify 2 só tem DOIS slots de tema (light/dark), mas temos TRÊS modos.
 * Salvia e Clean são ambos claros, então disputam o mesmo slot `light` — por
 * isso a paleta precisa ser reescrita em runtime ao trocar de modo, senão o
 * `color="primary"` fica preso na cor do outro tema.
 */
const PALETAS = {
  salvia: {
    primary: "#3E8E73",
    secondary: "#1F4D3F",
    accent: "#E8A04B",
    info: "#1F4D3F",
    primaryDeep: "#2F7359",
    primarySurface: "#EAF3EE",
  },
  light: {
    primary: "#1976d2",
    secondary: "#0d47a1",
    accent: "#82b1ff",
    info: "#1976d2",
    primaryDeep: "#1565c0",
    primarySurface: "#e3f2fd",
  },
  dark: {
    primary: "#5FB595",
    secondary: "#8FCFB0",
    accent: "#E8A04B",
    info: "#8FCFB0",
    primaryDeep: "#4A9E80",
    primarySurface: "#1E2A24",
  },
};

const STORAGE_KEY = "app_theme";

export const THEMES = [
  {
    value: "salvia",
    label: "Salvia",
    description: "Verde e off-white (padrão)",
    icon: "mdi-leaf",
    swatch: "#3E8E73",
    dark: false,
  },
  {
    value: "light",
    label: "Clean",
    description: "Azul e branco, como antes",
    icon: "mdi-white-balance-sunny",
    swatch: "#1976d2",
    dark: false,
  },
  {
    value: "dark",
    label: "Escuro",
    description: "Pinheiro escurecido",
    icon: "mdi-weather-night",
    swatch: "#1A241F",
    dark: true,
  },
];

const DEFAULT_THEME = "salvia";
const VALID = THEMES.map((t) => t.value);

/** Estado reativo compartilhado (o seletor da navbar lê daqui). */
export const themeState = Vue.observable({ current: DEFAULT_THEME });

export function getStoredTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(saved) ? saved : DEFAULT_THEME;
  } catch (e) {
    // localStorage pode estar indisponível (modo privativo/SSR)
    return DEFAULT_THEME;
  }
}

export function getThemeMeta(value) {
  return THEMES.find((t) => t.value === value) || THEMES[0];
}

/**
 * Aplica o tema no documento (e no Vuetify, se informado).
 * @param {string} theme
 * @param {object|null} vuetify instância `vuetify.framework` para sincronizar
 */
export function applyTheme(theme, vuetify = null) {
  const value = VALID.includes(theme) ? theme : DEFAULT_THEME;
  const meta = getThemeMeta(value);

  // Salvia é o padrão do :root — não precisa de atributo.
  const root = document.documentElement;
  if (value === DEFAULT_THEME) {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", value);
  }

  themeState.current = value;

  if (vuetify) {
    // Escreve a paleta no slot correspondente ANTES de alternar o dark,
    // para não haver um frame com a cor do modo anterior.
    const slot = meta.dark ? "dark" : "light";
    Object.assign(vuetify.theme.themes[slot], PALETAS[value] || {});
    vuetify.theme.dark = meta.dark;
  }

  return value;
}

/** Troca o tema, persiste e aplica. */
export function setTheme(theme, vuetify = null) {
  const value = applyTheme(theme, vuetify);
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch (e) {
    // sem persistência: o tema ainda vale para esta sessão
  }
  return value;
}

/** Chamado no boot, antes de montar a app (evita flash do tema errado). */
export function initTheme(vuetify = null) {
  return applyTheme(getStoredTheme(), vuetify);
}
